// Verbatim port of legacy/EMR Forms.html's <body> inner markup (everything
// except the <script> tag itself). The container element passed to
// mountBuilder already carries the `.builder-root` class that scopes
// builder.css, so this string only holds the direct children of <body>
// (toast-root, topbar, main w/ builder+runtime+responses+saved sections,
// and the three modals: block-dialog, delete-confirm, edit-response).
export const BUILDER_TEMPLATE = /* html */ `
<div id="toast-root" aria-live="polite"></div>
<header class="topbar">
  <div class="brand" style="gap:12px">
    <a href="/" title="Back to All Forms" style="display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:9px;border:1px solid var(--border);background:var(--s1);color:var(--txt3);text-decoration:none;transition:background var(--dur),color var(--dur),border-color var(--dur);flex-shrink:0" onmouseover="this.style.background='var(--brand-dim)';this.style.color='var(--brand)';this.style.borderColor='color-mix(in oklab,var(--brand) 30%,transparent)'" onmouseout="this.style.background='var(--s1)';this.style.color='var(--txt3)';this.style.borderColor='var(--border)'">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
    </a>
    <img src="/emr-logo.svg" alt="Imarc Forms" class="brand-logo" onerror="this.style.display='none'">
  </div>
  <div class="tabbar" role="tablist">
    <button class="tab" role="tab" aria-selected="true" data-tab="builder">📋 Builder</button>
    <button class="tab" role="tab" aria-selected="false" data-tab="runtime">▶ Preview</button>
    <button class="tab" role="tab" aria-selected="false" data-tab="responses">📊 Responses <span class="tab-dot" id="resp-dot"></span></button>
    <button class="tab" role="tab" aria-selected="false" data-tab="saved">💾 Saved Forms</button>
  </div>
  <div class="topbar-end" style="gap:8px">
    <span class="server-status offline" id="server-status" title="Checking server…"><span class="server-dot"></span><span id="server-status-lbl">Offline</span></span>
    <span class="badge" id="q-count-badge">0 questions</span>
    <button class="btn ghost sm" id="btn-signout" title="Sign out">Sign out</button>
  </div>
</header>
<main class="wrap">
<!-- BUILDER -->
<section id="view-builder" class="grid-3" role="tabpanel">
  <div class="card">
    <div class="card-head" style="padding-bottom:12px"><span class="card-title">Fields</span><span class="txt-xs muted2">Click or drag</span></div>
    <div class="card-body" style="padding-top:0"><div class="palette-scroll" id="palette-root"></div></div>
  </div>
  <div class="card">
    <div class="card-head" style="flex-direction:column;align-items:stretch;gap:0;padding-bottom:0">
      <div class="row" style="justify-content:flex-end;gap:8px;padding-bottom:14px;margin-bottom:16px;border-bottom:1px solid var(--border)">
        <button class="btn ghost sm hide" id="collapse-all" title="Collapse every question card to its header">⊟ Collapse all</button>
        <button class="btn ghost sm" id="clear">Clear</button>
        <button class="btn sm" id="save-form">💾 Save</button>
        <button class="btn sm" id="download-form" disabled title="Save the form with a unique name to enable download">⬇ Download</button>
        <button class="btn primary" id="preview-btn">▶ Preview</button>
        <button class="btn sm primary" id="publish-btn" disabled title="Add at least one question with a label to publish">🌐 Publish</button>
      </div>
      <div class="logo-picker" id="logo-picker">
        <span class="logo-picker-lbl">Form branding logo — upload two & pick one <span class="req">*</span></span>
        <div class="logo-opts" id="logo-opts"></div>
        <input type="file" id="logo-file" accept="image/*" style="display:none">
      </div>
      <div class="cover-area" id="cover-area">
        <input type="file" id="cover-upload" accept="image/*,video/*" style="display:none">
        <div class="cover-placeholder" id="cover-placeholder">🖼 Add cover image or video</div>
        <img id="cover-img-preview" class="cover-preview-img hide" alt="Cover">
        <video id="cover-vid-preview" class="cover-preview-vid hide" controls></video>
        <button class="cover-remove hide" id="cover-remove">✕ Remove</button>
      </div>
      <input class="canvas-title" type="text" id="form-title" placeholder="Untitled Form" value="Untitled Form">
      <textarea class="canvas-desc" id="form-desc" placeholder="Add a description… (supports 80+ words)"></textarea>
    </div>
    <div class="card-body">
      <div id="dropzone" class="dropzone">
        <div class="dropzone-empty" id="dz-empty">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 11h-2v-2h2v2zm0-4h-2V7h2v3z" opacity=".3"/></svg>
          <p><strong>Click a field</strong> in the left panel or <strong>drag it here</strong> to build your form.</p>
        </div>
      </div>
    </div>
  </div>
  <div class="card">
    <div class="card-head" style="padding-bottom:12px"><span class="card-title">Settings</span></div>
    <div class="card-body" style="padding-top:0;display:grid;gap:13px">
      <div class="panel-section">
        <div class="panel-title">Sections <button class="btn sm" id="addSection" type="button">+ Add</button></div>
        <p style="font-size:10.5px;color:var(--txt3);line-height:1.5;margin-bottom:8px;padding:5px 8px;background:var(--brand-dim);border-radius:6px;border-left:2px solid var(--brand)">Questions placed in sections will follow the sequence of the sections.</p>
        <div id="sectionsWrap" style="display:grid;gap:8px"></div>
      </div>
      <textarea id="json" style="display:none" spellcheck="false"></textarea>
      <div class="panel-section">
        <div class="panel-title">Engagement Details</div>
        <p class="txt-xs muted2" style="margin-bottom:8px;line-height:1.5">Optional — shown to respondents under the description when filled in.</p>
        <div style="display:grid;gap:8px">
          <label class="txt-xs muted2" style="display:block">Client Name
            <input type="text" id="client-name-inp" placeholder="e.g. Acme Foods Pvt. Ltd." style="margin-top:4px">
          </label>
          <label class="txt-xs muted2" style="display:block">Project Name
            <input type="text" id="project-name-inp" placeholder="e.g. Packaging cost benchmarking" style="margin-top:4px">
          </label>
          <label class="txt-xs muted2" style="display:block">Delivered Date
            <input type="date" id="delivered-date-inp" style="margin-top:4px">
          </label>
        </div>
      </div>
      <div class="panel-section">
        <div class="panel-title">Respondent Location</div>
        <div class="q-req-row">
          <span class="q-req-lbl" style="flex:1;line-height:1.4">📍 Capture GPS coordinates on submit</span>
          <label class="q-toggle">
            <input type="checkbox" id="capture-location-toggle">
            <span class="q-toggle-slider"></span>
          </label>
        </div>
        <p class="txt-xs muted2" style="margin-top:6px;line-height:1.5">When enabled, the respondent's browser location (latitude, longitude, accuracy) is recorded automatically on form submission. The respondent will be asked to grant location permission.</p>
      </div>
      <div class="panel-section">
        <div class="panel-title">Collaborators</div>
        <div class="row" style="gap:6px">
          <input type="email" id="collab-email-inp" placeholder="name@example.com" style="flex:1;font-size:12px;padding:6px 10px">
          <button class="btn sm primary" id="collab-add">Invite</button>
        </div>
        <div class="collab-list" id="collab-list"></div>
        <button class="btn sm" id="collab-export" style="margin-top:10px;width:100%">↓ Export form JSON for sharing</button>
        <p class="txt-xs muted2" style="margin-top:6px;line-height:1.5">Collaborators get Editor access. Export the form JSON and share it — they can import it via the JSON panel.</p>
      </div>
    </div>
  </div>
</section>
<!-- RUNTIME -->
<section id="view-runtime" class="hide" role="tabpanel">
  <div class="card runtime-card">
    <div class="card-body" style="padding:26px 30px">
      <div class="row" style="justify-content:space-between;align-items:center;margin-bottom:16px">
        <img src="/emr-logo.svg" alt="Imarc Forms" class="brand-logo rt-logo" onerror="this.style.display='none'">
        <button class="btn sm primary" id="publish-btn-rt" disabled title="Add at least one question with a label to publish">🌐 Publish</button>
      </div>
      <div class="progress-bar-wrap" id="rt-progress-wrap" style="display:none">
        <div class="progress-bar-row"><span id="rt-sec-label" class="muted2 txt-sm"></span><span id="rt-pct" class="muted2 txt-sm">0%</span></div>
        <div class="progress-bar-track"><div class="progress-bar-fill" id="rt-bar" style="width:0%"></div></div>
      </div>
      <div id="rt-logo-wrap"></div>
      <div id="rt-cover-wrap"></div>
      <div id="rt-sec-badge" class="rt-sec-badge hide"><span id="rt-sec-name"></span></div>
      <h2 id="rt-title" style="font-size:21px;font-weight:800;margin-bottom:4px"></h2>
      <p id="rt-desc" class="muted" style="margin-bottom:22px;font-size:13.5px"></p>
      <div id="rt-meta" class="rt-meta hide"></div>
      <div id="rt-fields"></div>
      <div class="rt-actions">
        <div class="rt-actions-left">
          <button class="btn ghost hide" id="rt-prev-sec">← Previous Section</button>
          <button class="btn ghost" id="rt-back">← Builder</button>
        </div>
        <div class="rt-actions-right">
          <button class="btn primary" id="rt-next">Continue →</button>
          <button class="btn primary hide" id="rt-submit">✓ Submit</button>
        </div>
      </div>
      <p class="txt-xs muted2" id="rt-progress" style="margin-top:8px"></p>
    </div>
  </div>
</section>
<!-- SUBMITTED (public fill page only — never used inside the authenticated builder) -->
<section id="view-submitted" class="hide" role="tabpanel">
  <div class="card runtime-card">
    <div class="card-body" style="padding:48px 30px;text-align:center">
      <div style="font-size:40px;margin-bottom:12px">✅</div>
      <h2 style="font-size:19px;font-weight:800;margin-bottom:6px">Thank you!</h2>
      <p class="muted txt-sm">Your response has been submitted.</p>
    </div>
  </div>
</section>
<!-- RESPONSES -->
<section id="view-responses" class="hide" role="tabpanel">
  <div class="card">
    <div class="card-body">
      <div class="row" style="justify-content:space-between;align-items:flex-start;margin-bottom:14px">
        <div><h2 style="font-size:17px;font-weight:800">Responses</h2><p class="muted txt-sm" id="resp-sub" style="margin-top:2px">0 submissions</p></div>
        <div class="row">
          <button class="btn excel" id="downloadXlsx">↓ Excel</button>
          <button class="btn" id="downloadCsv">↓ CSV</button>
        </div>
      </div>
      <div class="resp-stats">
        <div class="stat-card"><div class="stat-label">Total</div><div class="stat-value" id="stat-total">0</div></div>
        <div class="stat-card"><div class="stat-label">Questions</div><div class="stat-value" id="stat-qs">0</div></div>
      </div>
      <div class="resp-table-wrap">
        <table id="resp-table"><thead></thead><tbody></tbody></table>
      </div>
    </div>
  </div>
</section>
<!-- SAVED FORMS -->
<section id="view-saved" class="hide" role="tabpanel">
  <div class="card">
    <div class="card-body">
      <div class="row" style="justify-content:space-between;align-items:flex-start;margin-bottom:14px">
        <div><h2 style="font-size:17px;font-weight:800">Saved Forms</h2><p class="muted txt-sm" id="saved-sub" style="margin-top:2px">0 saved forms</p></div>
        <div style="display:flex;gap:8px">
          <button class="btn sm" id="saved-import" style="background:#22c55e;color:#fff;border-color:#22c55e">📥 Import Form</button>
          <input type="file" id="saved-import-file" accept=".html,.htm" style="display:none">
          <button class="btn sm" id="saved-refresh">↻ Refresh</button>
        </div>
      </div>
      <div id="saved-list"></div>
    </div>
  </div>
</section>
</main>
<!-- BLOCK DIALOG -->
<div id="block-dialog-overlay" class="modal-overlay hide">
  <div class="modal-box" style="max-width:440px">
    <div class="modal-body" style="text-align:center;padding:28px 28px 16px">
      <div class="block-dialog-icon">⛔</div>
      <div class="block-dialog-body" id="block-dialog-msg">You cannot proceed with this selection.</div>
    </div>
    <div class="modal-foot" style="justify-content:center">
      <button class="btn primary" id="block-dialog-ok" style="min-width:120px">OK, I understand</button>
    </div>
  </div>
</div>
<!-- DELETE FORM CONFIRM MODAL -->
<div id="del-confirm-overlay" class="modal-overlay hide">
  <div class="modal-box" style="max-width:440px">
    <div class="modal-body" style="text-align:center;padding:28px 28px 16px">
      <div class="block-dialog-icon">🗑️</div>
      <div class="block-dialog-body">Delete <strong id="del-confirm-name">this form</strong>?<br><span class="muted txt-sm">This action cannot be undone.</span></div>
    </div>
    <div class="modal-foot" style="justify-content:center;gap:10px">
      <button class="btn ghost" id="del-confirm-cancel" style="min-width:110px">Cancel</button>
      <button class="btn danger" id="del-confirm-ok" style="min-width:110px">Delete</button>
    </div>
  </div>
</div>
<!-- EDIT RESPONSE MODAL -->
<div id="edit-modal-overlay" class="modal-overlay hide">
  <div class="modal-box">
    <div class="modal-head">
      <h3>Edit Response</h3>
      <button class="btn sm ghost icon" id="edit-modal-close">✕</button>
    </div>
    <div class="modal-body" id="edit-modal-body"></div>
    <div class="modal-foot">
      <button class="btn ghost" id="edit-modal-cancel">Cancel</button>
      <button class="btn primary" id="edit-modal-save">Save changes</button>
    </div>
  </div>
</div>
<!-- PUBLISH MODAL -->
<div id="publish-modal-overlay" class="modal-overlay hide">
  <div class="modal-box" style="max-width:480px">
    <div class="modal-head">
      <h3 id="publish-modal-title">Form published</h3>
      <button class="btn sm ghost icon" id="publish-modal-close">✕</button>
    </div>
    <div class="modal-body">
      <p class="txt-sm muted2" style="margin-bottom:10px">Anyone with this link can open and submit this form — no sign-in required.</p>
      <div class="row" style="gap:8px">
        <input type="text" id="publish-link-inp" readonly style="flex:1;font-size:12px">
        <button class="btn sm primary" id="publish-link-copy">Copy</button>
      </div>
    </div>
    <div class="modal-foot" style="justify-content:space-between">
      <button class="btn ghost" id="publish-unpublish-btn">Unpublish</button>
      <button class="btn primary" id="publish-modal-done">Done</button>
    </div>
  </div>
</div>
`;
